import { cfApi } from "./client"
import type { paths } from "./generated/api"
import type {
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser"

type LoginSuccessResponse = NonNullable<
  paths["/api/auth/login"]["post"]["responses"][200]["content"]["application/json"]
>

// Straight from the OpenAPI schema, so a route change surfaces as a type error
// rather than silently making `token` or `isNewUser` absent — the latter gates
// the "UserSignup" conversion, so a silent shape change would stop counting
// conversions with nothing failing.
export type GoogleLoginSuccessResponse = NonNullable<
  paths["/api/auth/google/login"]["post"]["responses"][200]["content"]["application/json"]
>

export type AppleLoginSuccessResponse = NonNullable<
  paths["/api/auth/apple/login"]["post"]["responses"][200]["content"]["application/json"]
>

type ForgotPasswordSuccessResponse = NonNullable<
  paths["/api/auth/forgot-password"]["post"]["responses"][200]["content"]["application/json"]
>

type RegisterSuccessResponse = NonNullable<
  paths["/api/auth/register"]["post"]["responses"][200]["content"]["application/json"]
>

type SignInPhotosResponse = NonNullable<
  paths["/api/auth/sign-in-photos"]["get"]["responses"][200]["content"]["application/json"]
>

export type SignInPhoto = SignInPhotosResponse["photos"][number]

// Manual type definitions for endpoints not in generated API types
type RequestOTPSuccessResponse = {
  success: boolean
  hasPasskey?: boolean
  message?: string
}

type VerifyOTPSuccessResponse = {
  success: boolean
  token: string
  message?: string
  shouldSetupPasskey?: boolean
}

type PasskeyRegisterOptionsResponse = {
  success: boolean
  options?: Record<string, unknown>
}

type PasskeyRegisterVerifyResponse = {
  success: boolean
  verified?: boolean
}

type PasskeyAuthOptionsResponse = {
  success: boolean
  options?: Record<string, unknown>
}

type PasskeyAuthVerifyResponse = {
  success: boolean
  token?: string
  session?: Record<string, unknown>
}

type MeSuccessResponse = NonNullable<
  paths["/api/auth/me"]["get"]["responses"][200]["content"]["application/json"]
>

/**
 * Straight from the OpenAPI contract, so a server-side field change surfaces
 * here as a type error rather than being silently dropped. `utm` carries the ad
 * attribution from lib/adCapture.ts — Zion stores it as create-only HubSpot
 * contact properties, so registration is the only moment it can be recorded.
 */
export type RegisterCredentials = NonNullable<
  paths["/api/auth/register"]["post"]["requestBody"]
>["content"]["application/json"]

type GoogleLoginBody = NonNullable<
  paths["/api/auth/google/login"]["post"]["requestBody"]
>["content"]["application/json"]

type SocialProbeBody = NonNullable<
  paths["/api/auth/social/probe"]["post"]["requestBody"]
>["content"]["application/json"]

export type SocialProbeResponse =
  paths["/api/auth/social/probe"]["post"]["responses"][200]["content"]["application/json"]

/**
 * Name, phone and terms consent from registration page 2. Sent with the social
 * login call, which doubles as sign-up; omitted entirely for a plain sign-in.
 */
export type SocialSignUpDetails = Pick<
  GoogleLoginBody,
  "firstName" | "lastName" | "phoneNumber" | "acceptedTerms" | "mobileApp"
>

type AppleLoginBody = NonNullable<
  paths["/api/auth/apple/login"]["post"]["requestBody"]
>["content"]["application/json"]

const authApi = {
  login: async (credentials: { username: string; password: string }) => {
    return await cfApi.post<LoginSuccessResponse>(
      `/api/auth/login`,
      credentials
    )
  },
  /**
   * Google sign-in, which doubles as sign-up: the endpoint creates the Zion
   * user when this email has none yet. `utm` is sent for that case — Zion
   * writes attribution onto the HubSpot contact as create-only properties, so
   * account creation is the only moment it can be recorded. Ignored when the
   * user already exists.
   */
  googleLogin: async (
    idToken: string,
    utm?: GoogleLoginBody["utm"],
    details?: SocialSignUpDetails
  ): Promise<GoogleLoginSuccessResponse> => {
    // Call our custom Google login endpoint that handles account linking
    return await cfApi.post<GoogleLoginSuccessResponse>(
      `/api/auth/google/login`,
      { idToken, ...(utm ? { utm } : {}), ...(details ?? {}) }
    )
  },
  /**
   * Verify a social ID token and report whether the member already exists,
   * WITHOUT creating anything. Registration page 1 uses this to decide between
   * completing a sign-in and routing a new member to page 2 for name, phone and
   * terms consent — the login endpoints provision on first contact, so probing
   * is the only way to collect consent before the account exists.
   */
  socialProbe: async (
    provider: SocialProbeBody["provider"],
    idToken: string
  ): Promise<SocialProbeResponse> => {
    return await cfApi.post<SocialProbeResponse>(`/api/auth/social/probe`, {
      provider,
      idToken,
    })
  },
  /**
   * Apple sign-in, which doubles as sign-up. `name` comes from Apple's
   * first-consent payload and `utm` from the landing URL; both are only used
   * when this call creates the Zion user, and neither is available afterwards.
   */
  appleLogin: async (
    idToken: string,
    opts?: Pick<AppleLoginBody, "name" | "utm">,
    details?: SocialSignUpDetails
  ): Promise<AppleLoginSuccessResponse> => {
    // Call our custom Apple login endpoint that handles account linking
    return await cfApi.post<AppleLoginSuccessResponse>(
      `/api/auth/apple/login`,
      {
        idToken,
        ...(opts?.name ? { name: opts.name } : {}),
        ...(opts?.utm ? { utm: opts.utm } : {}),
        ...(details ?? {}),
      }
    )
  },
  /**
   * Member photos shown on the sign-in screen background. Public endpoint —
   * no auth token required. Returns an empty list when the integration is
   * unconfigured or upstream fails, so callers should fall back to a static
   * image.
   */
  signInPhotos: async (): Promise<SignInPhotosResponse> => {
    return await cfApi.get<SignInPhotosResponse>(`/api/auth/sign-in-photos`)
  },
  me: async () => {
    // Use /api/auth/me endpoint which returns full user data including hsContactId
    const response = await cfApi.get<MeSuccessResponse>(`/api/auth/me`)

    if (!response.user) {
      throw new Error("Not authenticated")
    }

    // Return the user data directly - it already has all fields including hsContactId
    return {
      success: true,
      user: {
        id: response.user.id,
        email: response.user.email,
        contactEmail: response.user.contactEmail,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        phoneNumber: response.user.phoneNumber,
        referralCode: response.user.referralCode,
        hsContactId: response.user.hsContactId,
        rewardsEligible: response.user.rewardsEligible,
        rewardsOptInStatus: response.user.rewardsOptInStatus,
        rewardsEligibleDealCount: response.user.rewardsEligibleDealCount,
        buyerPersona: response.user.buyerPersona,
        repeatBorrower: response.user.repeatBorrower,
        isBroker: response.user.isBroker,
        googleReviewEligible: response.user.googleReviewEligible,
        zionAccessToken: response.user.zionAccessToken,
        isSuperuser: response.user.isSuperuser,
        hubspotOwnerId: response.user.hubspotOwnerId,
      },
    }
  },
  forgotPassword: async (
    email: string
  ): Promise<ForgotPasswordSuccessResponse> => {
    return await cfApi.post<ForgotPasswordSuccessResponse>(
      `/api/auth/forgot-password`,
      { email }
    )
  },
  register: async (
    credentials: RegisterCredentials
  ): Promise<RegisterSuccessResponse> => {
    return await cfApi.post<RegisterSuccessResponse>(
      `/api/auth/register`,
      credentials
    )
  },
  requestOTP: async (
    email: string,
    forceOTP = false
  ): Promise<RequestOTPSuccessResponse> => {
    // Use better-auth email-otp endpoint
    return await cfApi.post<RequestOTPSuccessResponse>(
      `/api/auth/email-otp/send-verification-otp`,
      { email, type: "sign-in", forceOTP }
    )
  },
  verifyOTP: async (
    email: string,
    code: string
  ): Promise<VerifyOTPSuccessResponse> => {
    // Use better-auth sign-in with OTP endpoint
    // This endpoint signs the user in (or creates account if first time)
    const response = await cfApi.post<{
      token: string
      user: { id: string; email: string; name: string | null }
    }>(`/api/auth/sign-in/email-otp`, { email, otp: code })

    // Transform better-auth response to match frontend expectations
    // Note: shouldSetupPasskey is checked after token is set (in login page)
    return {
      success: true,
      token: response.token,
      message: "Login successful",
      shouldSetupPasskey: false, // Will be checked after auth token is set
    }
  },

  // Check if user has any passkeys (must be called after auth token is set)
  hasPasskeys: async (): Promise<boolean> => {
    try {
      const passkeysResponse = await cfApi.get<
        Array<{ id: string; name: string | null }>
      >(`/api/auth/passkey/list-user-passkeys`)
      return passkeysResponse.length > 0
    } catch (error) {
      // Assume they have passkeys to avoid unnecessary prompts on error
      return true
    }
  },

  // Passkey methods - Use better-auth endpoints via cfApi
  passkeyRegisterOptions: async (): Promise<PasskeyRegisterOptionsResponse> => {
    // Note: better-auth uses POST /api/auth/passkey/add-passkey for registration
    // This is kept for backward compatibility but should migrate to add-passkey
    return await cfApi.post<PasskeyRegisterOptionsResponse>(
      `/api/auth/passkey/generate-registration-options`,
      {}
    )
  },
  passkeyRegisterVerify: async (data: {
    response: RegistrationResponseJSON
    challenge: string
    name?: string
  }): Promise<PasskeyRegisterVerifyResponse> => {
    return await cfApi.post<PasskeyRegisterVerifyResponse>(
      `/api/auth/passkey/add-passkey`,
      data
    )
  },
  passkeyAuthOptions: async (data: {
    email?: string
  }): Promise<PasskeyAuthOptionsResponse> => {
    // For sign-in, better-auth uses the sign-in endpoint
    return await cfApi.post<PasskeyAuthOptionsResponse>(
      `/api/auth/sign-in/passkey`,
      data
    )
  },
  passkeyAuthVerify: async (data: {
    response: AuthenticationResponseJSON
    challenge: string
  }): Promise<PasskeyAuthVerifyResponse> => {
    return await cfApi.post<PasskeyAuthVerifyResponse>(
      `/api/auth/sign-in/passkey`,
      data
    )
  },

  // New better-auth passkey helpers using cfApi
  /**
   * Register a new passkey for the authenticated user
   * @param name Optional name/label for the passkey
   */
  addPasskey: async (name?: string) => {
    return await cfApi.post(`/api/auth/passkey/add-passkey`, { name })
  },

  /**
   * Sign in with passkey
   */
  signInWithPasskey: async () => {
    return await cfApi.post(`/api/auth/sign-in/passkey`, {})
  },

  /**
   * List all passkeys for the authenticated user
   */
  listPasskeys: async () => {
    return await cfApi.get(`/api/auth/passkey/list-user-passkeys`)
  },

  /**
   * Delete a passkey by ID
   */
  deletePasskey: async (id: string) => {
    return await cfApi.post(`/api/auth/passkey/delete-passkey`, { id })
  },
}

export default authApi
